import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle, Play, RotateCw, Trash2, Ban } from "lucide-react";

const ACTION_COLOR = { create: "text-emerald-600", update: "text-blue-600", delete: "text-red-600", noop: "text-zinc-400", error: "text-red-500" };

export default function ImportExport() {
  const { t } = useLang();
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState("employees");
  const [jobs, setJobs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const loadJobs = useCallback(() => api.get("/data-jobs").then((r) => setJobs(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/import-export/entities").then((r) => setEntities(r.data)).catch(() => {});
    loadJobs(); const iv = setInterval(loadJobs, 5000); return () => clearInterval(iv);
  }, [loadJobs]);

  const cur = entities.find((e) => e.entity === entity);

  const runExport = async () => {
    setBusy(true);
    try { await api.post(`/export/${entity}`); toast.success(t("ie.exportStarted")); loadJobs(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const download = (jid) => {
    fetch(`${API}/export/${jid}/download`, { credentials: "include" })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = jobs.find((j) => j.id === jid)?.filename || "export.csv"; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error(t("ie.downloadFailed")));
  };

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setBusy(true); setPreview(null); setExcluded(new Set());
      try {
        const { data } = await api.post(`/import/${entity}/preview`, { csv: ev.target.result });
        setPreview(data);
        if (!cur?.importSupported) toast.warning(t("ie.exportOnly"));
      } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
    };
    reader.readAsText(file);
  };

  const toggleRow = (i) => setExcluded((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const execute = async () => {
    setBusy(true);
    try {
      await api.post(`/import/${preview.id}/execute`, { exclude: [...excluded] });
      toast.success(t("ie.importStarted")); setPreview(null); loadJobs();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const cancelImport = async () => {
    setBusy(true);
    try { await api.post(`/import/${preview.id}/cancel`); toast.info(t("ie.cancelled")); setPreview(null); loadJobs(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const rerunJob = async (id) => {
    try { await api.post(`/data-jobs/${id}/rerun`); toast.success(t("ie.rerunStarted")); loadJobs(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteJob = async (id) => {
    try { await api.delete(`/data-jobs/${id}`); toast.success(t("ie.deleted")); loadJobs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const actionableRows = preview ? preview.rows.filter((r) => ["create", "update", "delete"].includes(r.action) && !r.error) : [];
  const allSelected = actionableRows.length > 0 && actionableRows.every((r) => !excluded.has(r.i));
  const selectAll = () => setExcluded(new Set());
  const selectNone = () => setExcluded(new Set(actionableRows.map((r) => r.i)));

  const actionable = actionableRows.filter((r) => !excluded.has(r.i)).length;

  return (
    <div className="space-y-8">
      <PageHeader title={t("ie.title")} subtitle={t("ie.subtitle")} />

      <div className="flex flex-wrap items-end gap-4 border border-zinc-200 bg-white p-5">
        <div className="space-y-1.5">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ie.entity")}</div>
          <Select value={entity} onValueChange={(v) => { setEntity(v); setPreview(null); }}>
            <SelectTrigger className="w-64 rounded-sm border-zinc-300 text-sm" data-testid="ie-entity-select"><SelectValue /></SelectTrigger>
            <SelectContent>{entities.map((e) => <SelectItem key={e.entity} value={e.entity}>{t(`ie.ent.${e.entity}`)}{!e.importSupported ? " (export only)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={runExport} disabled={busy} data-testid="ie-export-btn" variant="outline" className="rounded-sm border-zinc-300">
          <Download size={16} className="mr-2" /> {t("ie.export")}
        </Button>
        <label className={`inline-flex cursor-pointer items-center rounded-sm px-4 py-2 text-sm font-semibold ${cur?.importSupported ? "bg-zinc-900 text-white hover:bg-zinc-800" : "cursor-not-allowed bg-zinc-200 text-zinc-400"}`} data-testid="ie-import-label">
          <Upload size={16} className="mr-2" /> {t("ie.importCsv")}
          <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={!cur?.importSupported || busy} className="hidden" data-testid="ie-import-input" />
        </label>
        {cur && <div className="text-[11px] text-zinc-400">{t("ie.columns")}: <span className="font-mono">{cur.columns.join(", ")}</span></div>}
      </div>

      {preview && (
        <div className="border border-zinc-200 bg-white" data-testid="ie-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {Object.entries(preview.summary).map(([k, v]) => v > 0 && (
                <span key={k} className={`font-semibold ${ACTION_COLOR[k]}`} data-testid={`ie-sum-${k}`}>{v} {t(`ie.${k}`)}</span>
              ))}
              {actionableRows.length > 0 && (
                <span className="flex items-center gap-2 border-l border-zinc-200 pl-3 text-xs">
                  <button onClick={selectAll} data-testid="ie-select-all" className="font-semibold text-zinc-600 hover:text-zinc-900 hover:underline">{t("ie.selectAll")}</button>
                  <span className="text-zinc-300">·</span>
                  <button onClick={selectNone} data-testid="ie-select-none" className="font-semibold text-zinc-600 hover:text-zinc-900 hover:underline">{t("ie.selectNone")}</button>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={cancelImport} disabled={busy} variant="outline" data-testid="ie-cancel-btn" className="rounded-sm border-zinc-300 text-zinc-600 hover:bg-red-50 hover:text-red-600">
                <XCircle size={16} className="mr-2" /> {t("ie.cancelImport")}
              </Button>
              <Button onClick={execute} disabled={busy || actionable === 0} data-testid="ie-execute-btn" className="rounded-sm bg-zinc-900 text-white hover:bg-zinc-800">
                {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}{t("ie.runImport")} ({actionable})
              </Button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <tr><th className="px-3 py-2 w-8">{actionableRows.length > 0 && <Checkbox checked={allSelected} onCheckedChange={(v) => v ? selectAll() : selectNone()} data-testid="ie-select-all-checkbox" />}</th><th className="px-3 py-2">{t("ie.action")}</th><th className="px-3 py-2">{t("ie.key")}</th><th className="px-3 py-2">{t("ie.detail")}</th></tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.i} className="border-b border-zinc-100 last:border-0" data-testid={`ie-row-${r.i}`}>
                    <td className="px-3 py-2">{["create", "update", "delete"].includes(r.action) && !r.error && <Checkbox checked={!excluded.has(r.i)} onCheckedChange={() => toggleRow(r.i)} data-testid={`ie-row-check-${r.i}`} />}</td>
                    <td className={`px-3 py-2 font-bold uppercase ${ACTION_COLOR[r.action]}`}>{t(`ie.${r.action}`)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-700">{r.key || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-500">
                      {r.error ? <span className="text-red-500">{r.error}</span>
                        : r.changes ? Object.entries(r.changes).map(([f, c]) => <span key={f} className="mr-3">{f}: <span className="text-zinc-400 line-through">{c.from || "—"}</span> → <span className="text-zinc-800">{c.to}</span></span>)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("ie.jobs")}</div>
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="px-4 py-2">{t("ie.jobType")}</th><th className="px-4 py-2">{t("ie.entity")}</th><th className="px-4 py-2">{t("ie.status")}</th><th className="px-4 py-2">{t("ie.result")}</th><th className="px-4 py-2 text-right"></th></tr>
            </thead>
            <tbody>
              {jobs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400">{t("ie.noJobs")}</td></tr>}
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`ie-job-${j.id}`}>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 font-semibold text-zinc-800">{j.kind === "export" ? <Download size={14} /> : <FileSpreadsheet size={14} />}{t(`ie.${j.kind}`)}</span></td>
                  <td className="px-4 py-3 text-zinc-600">{j.entity}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase">
                      {j.status === "done" ? <CheckCircle2 size={13} className="text-emerald-500" /> : j.status === "error" ? <XCircle size={13} className="text-red-500" /> : j.status === "cancelled" ? <Ban size={13} className="text-zinc-400" /> : <Loader2 size={13} className="animate-spin text-amber-500" />}
                      {t(`ie.st.${j.status}`) || j.status}{j.status === "running" && j.total ? ` ${j.done || 0}/${j.total}` : ""}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {j.kind === "export" ? (j.rowCount != null ? `${j.rowCount} rows` : "—") : (j.ok != null ? `${j.ok} ok, ${j.failed || 0} failed` : (j.summary ? `${j.summary.create}c / ${j.summary.update}u / ${j.summary.delete}d` : "—"))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {j.kind === "export" && j.status === "done" && <button onClick={() => download(j.id)} data-testid={`ie-download-${j.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"><Download size={13} /> {t("ie.download")}</button>}
                      {!["running", "queued"].includes(j.status) && <button onClick={() => rerunJob(j.id)} data-testid={`ie-rerun-${j.id}`} title={t("ie.rerun")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:underline"><RotateCw size={13} /> {t("ie.rerun")}</button>}
                      {!["running", "queued"].includes(j.status) && <button onClick={() => deleteJob(j.id)} data-testid={`ie-delete-${j.id}`} title={t("ie.delete2")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-red-600 hover:underline"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
